import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reports } from '@/api/documents';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function ProductionWipReportPage() {
  const {
    availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs, loading: permsLoading,
  } = usePermissionsByOrg('reports.regulated.view');
  const [orgId, setOrgId] = useState('');
  const [asOf, setAsOf] = useState<string>(() => todayIso());

  if (!orgId && availableOrgs[0]) setOrgId(availableOrgs[0].id);

  const q = useQuery({
    queryKey: ['production-wip', orgId, asOf],
    queryFn: () => reports.productionWip({ organization_id: orgId, as_of: asOf }),
    enabled: !!orgId,
  });

  if (!permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>Незавершене виробництво (НЗВ)</h2>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)', maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав переглядати звіти у жодній з ваших організацій
            ({totalAccessibleOrgs}). Зверніться до адміністратора.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Незавершене виробництво (НЗВ)</h2>
        <button onClick={() => q.refetch()}>оновити</button>
      </div>
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        Залишок незавершеного виробництва (рах. 23) у розрізі <b>підрозділ × номенклатурна
        група</b> станом на дату. Накопичувально: опеннінг + матеріали − випуск − закриття.
      </p>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Юрособа</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {availableOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Станом на дату</label>
          <DatePopover value={asOf} onChange={(v) => setAsOf(v.slice(0, 10))} />
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem' }}>Підрозділ</th>
            <th style={{ padding: '0.5rem' }}>Номенклатурна група</th>
            <th style={{ padding: '0.5rem', textAlign: 'right', width: 200 }}>Залишок НЗВ</th>
          </tr>
        </thead>
        <tbody>
          {q.isLoading && (
            <tr><td colSpan={3} style={{ padding: '1rem', color: 'var(--muted)' }}>Завантаження…</td></tr>
          )}
          {!q.isLoading && (q.data?.rows.length ?? 0) === 0 && (
            <tr><td colSpan={3} style={{ padding: '1rem', color: 'var(--muted)' }}>
              Немає незавершеного виробництва станом на цю дату.
            </td></tr>
          )}
          {q.data?.rows.map((r, i) => (
            <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '0.5rem' }}>{r.department_name ?? '— (без підрозділу)'}</td>
              <td style={{ padding: '0.5rem' }}>{r.nomenclature_group_name ?? '— (без групи)'}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(r.balance)}</td>
            </tr>
          ))}
        </tbody>
        {q.data && q.data.rows.length > 0 && (
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border-strong)', fontWeight: 600 }}>
              <td style={{ padding: '0.5rem' }} colSpan={2}>Разом НЗВ</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(q.data.total)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
